import { createContext, useContext, useState, ReactNode, FC } from "react"
import styled from "styled-components"
import { useTranslation } from "react-i18next"
import WhiteSpace from "./components/Whitespace"
import { BREAKPOINTS, TAP_MIN } from "./GlobalTheme"

type ConfirmOptions = {
  message: string
  showCancel?: boolean
}

type ConfirmContextType = (message: string | ConfirmOptions) => Promise<boolean>

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10002;
`

const Dialog = styled.div`
  background: #F9F9F9;
  padding: 24px;
  border-radius: 8px;
  min-width: 300px;
  max-width: 30vw;
  box-shadow: 0px 4px 4px -4px #0C0C0D0D;
  border-radius: 24px;

  @media (max-width: ${BREAKPOINTS.shell}px) {
    /* min-width 300px + 48px padding overflows a 390px screen, and there was no max-height/overflow
       at all, so a long message was clipped with body{overflow:hidden} and unreachable. */
    min-width: 0px;
    width: calc(100vw - 32px);
    max-width: none;
    max-height: calc(100dvh - 32px);
    overflow-y: auto;
    box-sizing: border-box;
  }
`

const Message = styled.p`
  margin: 0;
  color: #333;
  font-size: 16px;
`

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  gap: 12px;
`

const CancelButton = styled.button`
  color: #333;
  font-weight: 600;
  outline: none;
  border: none;
  cursor: pointer;

  background-color: transparent;

  &:active {
    transform: scale(.99);
  }

  @media (max-width: ${BREAKPOINTS.shell}px) {
    min-height: ${TAP_MIN}px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`

const ConfirmButton = styled.button`
  color: white;
  font-weight: 600;
  border-radius: 12px;
  padding: 14px;
  padding-left: 18px;
  padding-right: 18px;
  outline: none;
  border: none;
  cursor: pointer;

  background-color: #4AB1FC;

  &:active {
    transform: scale(.99);
  }
`

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined)

export const useConfirm = (): ConfirmContextType => {
  const context = useContext(ConfirmContext)
  if (!context) {
    throw new Error("useConfirm must be used within a ConfirmProvider")
  }
  return context
}

type ConfirmState = {
  message: string
  showCancel: boolean
  resolve: (value: boolean) => void
} | null

export const ConfirmProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation()

  const [confirmState, setConfirmState] = useState<ConfirmState>(null)

  const confirm = (messageOrOptions: string | ConfirmOptions): Promise<boolean> => {
    const message = typeof messageOrOptions === "string" ? messageOrOptions : messageOrOptions.message
    const showCancel = typeof messageOrOptions === "string" ? true : messageOrOptions.showCancel ?? true

    return new Promise((resolve) => {
      setConfirmState({ message, showCancel, resolve })
    })
  }

  const handleConfirm = () => {
    confirmState?.resolve(true)
    setConfirmState(null)
  }

  const handleCancel = () => {
    confirmState?.resolve(false)
    setConfirmState(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {confirmState && (
        <Overlay>
          <Dialog>
            <Message>{confirmState.message}</Message>
            <ButtonRow>
              {confirmState.showCancel && (
                <>
                  <CancelButton onClick={handleCancel}>{t("general_cancel")}</CancelButton>
                  <WhiteSpace width="8px" />
                </>
              )}
              <ConfirmButton onClick={handleConfirm}>{confirmState.showCancel ? t("general_confirm") : t("general_ok")}</ConfirmButton>
            </ButtonRow>
          </Dialog>
        </Overlay>
      )}
    </ConfirmContext.Provider>
  )
}
